<script lang="ts" setup>
import { inject, ref, onMounted, watch } from 'vue';
import draggable from 'vuedraggable';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import TriStateCheckbox from '@/components/TriStateCheckbox.vue';
import MenuVerticalDots from '@/assets/images/icons8-menu-vertical.svg?component';

import type { Ref } from 'vue';
import type { Viewer, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestHotspot } from '@/types/IManifest';

type HotspotUpdate = { name: string, state: Nullable<boolean> };

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const activeHotspot = inject('activeHotspot') as Ref<Nullable<string>>;
const hotspot = inject('hotspot') as Ref<IManifestHotspot>;
const hotspotUpdates = ref<HotspotUpdate[]>([]);

const generateHotspotUpdatesArray = () => {
  // Need to refactor hotspotUpdates list generation.
  // Must ALWAYS include all hotspots, even if they are not in the manifest.
  // Anything in hotspotUpdates array must appear at top of list

  const hotspotUpdatesArray: HotspotUpdate[] = [];

  for (const hotspotInfo of hotspot.value.hotspotUpdates) {
    hotspotUpdatesArray.push({
      name: hotspotInfo[0],
      state: hotspotInfo[1],
    });
  }

  const hotspotEntries = Object.entries(manifest.value.hotspots)
    .sort(([, hotspotA], [, hotspotB]) => {
      if (hotspotA.order < hotspotB.order) {
        return -1;
      } else if (hotspotA.order > hotspotB.order) {
        return 1;
      } else {
        return 0;
      }
    });

  for (const [hotspotName] of hotspotEntries) {
    if (!hotspotUpdatesArray.some((hotspotUpdate) => hotspotUpdate.name === hotspotName)) {
      hotspotUpdatesArray.push({
        name: hotspotName,
        state: null,
      });
    }
  }

  hotspotUpdates.value = hotspotUpdatesArray;
};

const mountHandler = () => {
  if (!viewer.value) return;

  generateHotspotUpdatesArray();
};

const hotspotUpdatesWatcher = (newValue: HotspotUpdate[]) => {
  if (!activeHotspot.value) return;
  if (!Object.hasOwn(manifest.value.hotspots, activeHotspot.value)) return;

  manifest.value.hotspots[activeHotspot.value].hotspotUpdates = newValue
    .filter((hotspotUpdate) => hotspotUpdate.state !== null)
    .map((hotspotUpdate) => [hotspotUpdate.name, hotspotUpdate.state as boolean]);
};

const manifestHotspotsWatcher = () => {
  generateHotspotUpdatesArray();
};

const activeHotspotWatcher = () => {
  generateHotspotUpdatesArray();
};

onMounted(mountHandler);
watch(activeHotspot, activeHotspotWatcher);
watch(hotspotUpdates, hotspotUpdatesWatcher, { deep: true });
watch(() => Object.keys(manifest.value.hotspots), manifestHotspotsWatcher);
</script>

<template>
  <CollapseFieldset>
    <template #legend>
      Hotspot Updates
    </template>

    <div class="mb-1 pb-1 border-b border-b-slate-400 font-mono">
      <p class="text-xs">
        Unchecked = No change
      </p>
      <p class="text-xs">
        Checked = Show
      </p>
      <p class="text-xs">
        X = Hide
      </p>
    </div>

    <draggable v-model="hotspotUpdates" item-key="name" class="flex flex-col max-h-[500px] overflow-y-auto">
      <template #item="{element}">
        <div class="flex flex-row items-center">
          <MenuVerticalDots class="cursor-ns-resize" />
          <TriStateCheckbox v-model="element.state" :label="element.name" class="mb-1 last-of-type:mb-0" />
        </div>
      </template>
    </draggable>
  </CollapseFieldset>
</template>
